import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';

// Components
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Products from './components/Products';
import ProductShowcase from './components/ProductShowcase';
import Stats from './components/Stats';
import About from './components/About';
import CTA from './components/CTA';
import Footer from './components/Footer';
import AnimatedBackground from './components/AnimatedBackground';
import Contact from './components/Contact';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import CookiePolicy from './components/CookiePolicy';
import POPIACompliance from './components/POPIACompliance';
import AcceptableUsePolicy from './components/AcceptableUsePolicy';
import RefundPolicy from './components/RefundPolicy';
import Blog from './components/Blog';
import BlogPost from './components/BlogPost';
import Support from './components/Support';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ResetPassword from './components/auth/ResetPassword';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import Dashboard from './components/dashboard/Dashboard';
import Plans from './components/dashboard/Plans';
import Account from './components/dashboard/Account';
import Billing from './components/dashboard/Billing';
import PaymentLinkRedeem from './components/dashboard/PaymentLinkRedeem';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminUsers from './components/admin/AdminUsers';
import AdminPaymentLinks from './components/admin/AdminPaymentLinks';
import AdminPayments from './components/admin/AdminPayments';
import AdminHelpDesk from './components/admin/AdminHelpDesk';
import AdminAuditLogs from './components/admin/AdminAuditLogs';
import AdminVetAxis from './components/admin/AdminVetAxis';
import AdminVetAxisBackfill from './components/admin/AdminVetAxisBackfill';
import AdminVetAxisClinics from './components/admin/AdminVetAxisClinics';
import AdminVetAxisClinicUsers from './components/admin/AdminVetAxisClinicUsers';
import AdminVetAxisAuditLogs from './components/admin/AdminVetAxisAuditLogs';
import AdminVetAxisSyncLogs from './components/admin/AdminVetAxisSyncLogs';
import AdminProductOps from './components/admin/AdminProductOps';
import AdminProductUsers from './components/admin/AdminProductUsers';
import AdminProductAuditLogs from './components/admin/AdminProductAuditLogs';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
      <Router>
        <div className="App">
          <AnimatedBackground />
          <Navbar />

        <Routes>
          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/reset" element={<ResetPassword />} />

          {/* Suite dashboard */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/plans" element={<ProtectedRoute><Plans /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
          <Route path="/account/*" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/pay/:linkToken" element={<ProtectedRoute><PaymentLinkRedeem /></ProtectedRoute>} />

          {/* Admin routes */}
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
          <Route path="/admin/payment-links" element={<AdminRoute><AdminPaymentLinks /></AdminRoute>} />
          <Route path="/admin/payments" element={<AdminRoute><AdminPayments /></AdminRoute>} />
          <Route path="/admin/audit-logs" element={<AdminRoute><AdminAuditLogs /></AdminRoute>} />
          <Route path="/admin/vetaxis" element={<AdminRoute><AdminVetAxis /></AdminRoute>} />
          <Route path="/admin/vetaxis/clinics" element={<AdminRoute><AdminVetAxisClinics /></AdminRoute>} />
          <Route path="/admin/vetaxis/clinics/:clinicId" element={<AdminRoute><AdminVetAxisClinicUsers /></AdminRoute>} />
          <Route path="/admin/vetaxis/backfill" element={<AdminRoute><AdminVetAxisBackfill /></AdminRoute>} />
          <Route path="/admin/vetaxis/sync-logs" element={<AdminRoute><AdminVetAxisSyncLogs /></AdminRoute>} />
          <Route path="/admin/vetaxis/audit-logs" element={<AdminRoute><AdminVetAxisAuditLogs /></AdminRoute>} />
          <Route path="/admin/staffaxis" element={<AdminRoute><AdminProductOps productId="staffaxis" /></AdminRoute>} />
          <Route path="/admin/staffaxis/users" element={<AdminRoute><AdminProductUsers productId="staffaxis" /></AdminRoute>} />
          <Route path="/admin/staffaxis/audit-logs" element={<AdminRoute><AdminProductAuditLogs productId="staffaxis" /></AdminRoute>} />
          <Route path="/admin/noteaxis" element={<AdminRoute><AdminProductOps productId="noteaxis" /></AdminRoute>} />
          <Route path="/admin/noteaxis/users" element={<AdminRoute><AdminProductUsers productId="noteaxis" /></AdminRoute>} />
          <Route path="/admin/noteaxis/audit-logs" element={<AdminRoute><AdminProductAuditLogs productId="noteaxis" /></AdminRoute>} />
          <Route path="/admin/help-desk" element={<AdminRoute><AdminHelpDesk /></AdminRoute>} />

          <Route path="/" element={
            <motion.main
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Hero />
              <Products />
              <ProductShowcase />
              <Stats />
              <About />
              <CTA />
            </motion.main>
          } />

          {/* Additional routes */}
          <Route path="/products" element={<Products />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/support" element={<Support />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/popia" element={<POPIACompliance />} />
          <Route path="/acceptable-use" element={<AcceptableUsePolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
        </Routes>

        <Footer />
        </div>
      </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
